import * as React from 'react'
import * as plume from '@ulule/owl-kit-components/next'

import { Container } from '../../_shared/Container'
import {
  formatInteger,
  getAmountRaisedEurTotal,
  getProjectsFinancedTotal,
  type UluleStats,
} from '../../_shared/ululeStats'
import { CountUp } from '../components/CountUp'
import { HERO } from '../content'
import { useStatsTracking } from '../tracking'
import styles from './Hero.module.css'

type Figure = { value: number; suffix: string; label: string; tone: string }

/** All three headline figures are live, and all three aggregate Ulule + KissKissBankBank. */
function makeFigures(stats: UluleStats): Figure[] {
  return [
    {
      value: getProjectsFinancedTotal(stats),
      suffix: '',
      label: 'Projets financés',
      tone: styles.figOne,
    },
    { value: getAmountRaisedEurTotal(stats), suffix: ' €', label: 'Collectés', tone: styles.figTwo },
    { value: stats.usersCount, suffix: '', label: 'Contributeurs et contributrices', tone: styles.figThree },
  ]
}

export function Hero({ stats }: { stats: UluleStats | null }): React.ReactElement {
  const tracking = useStatsTracking()
  const figures = stats === null ? [] : makeFigures(stats)

  return (
    <section className={styles.hero}>
      <Container>
        <nav className={styles.hero__breadcrumb} aria-label="Fil d'Ariane">
          <a href="/about/ulule/">À propos</a>
          <span className={styles.hero__breadcrumbSep} aria-hidden="true">
            ›
          </span>
          <span className={styles.hero__breadcrumbTag}>{HERO.breadcrumbTag}</span>
        </nav>
        <h1 className={styles.hero__title}>
          {'Ulule '}
          <span className={styles.hero__titleHighlight}>en chiffres</span>
        </h1>

        {figures.length > 0 && (
          <>
            <div className={styles.figs}>
              {figures.map((figure) => (
                <div key={figure.label} className={styles.fig}>
                  <div className={`${styles.fig__num} ${figure.tone}`}>
                    <CountUp value={figure.value} suffix={figure.suffix} format={formatInteger} />
                  </div>
                  <span className={styles.fig__lbl}>{figure.label}</span>
                </div>
              ))}
            </div>
            <div className={styles.hero__foot}>
              <p>{HERO.foot}</p>
            </div>
          </>
        )}

        <div className={styles.hero__thanks}>
          {/* The space rides inside the string: a whitespace-only node between two tags is
              stripped by Django's MinifyHTMLMiddleware, gluing the heart to the last word. */}
          <p>
            {`${HERO.thanks} `}
            <span className={styles.hero__heart} aria-hidden="true">
              <plume.glyphs.fill.Heart size={18} />
            </span>
          </p>
          <p>
            {HERO.thanksCta.before}
            <a href={HERO.thanksCta.href} onClick={() => tracking.ctaClick('hero')}>
              {HERO.thanksCta.label}
            </a>
            {HERO.thanksCta.after}
          </p>
        </div>
      </Container>
      <div className={styles.hero__rule} />
    </section>
  )
}
